import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableHeader } from "@app/directives/_layout/v-table/_models/IVTableHeader";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { IReportHistory } from "@app/main/components/reports/models/IDynamicReportLog";
import { IVButtonDropDownItem } from "@directives/_core/v-button/v-button.component";
import "./filter-bar.component.scss";
import { FilterBarItemSearchPredicate, FilterBarListIterable, FilterChange, FilterTriggerType, IAggregateFilterEvent, IFilterBarChange, IFilterBarFilter, IFilterBarListWithCount, IFilterBarTab, IVerticalFilterListItem } from "./filter-bar.models";
import { FilterBarService } from "./filter-bar.service";

@Component({
    selector: "filter-bar",
    template: require("./filter-bar.component.html")
})
export class FilterBarComponent<T = any> implements IFilterBarChange<IAggregateFilterEvent<T>>, OnInit, OnChanges, AfterViewInit {

    @Input() compact = true;

    @Input() tabs: IFilterBarTab<T>[] = [];
    @Input() hideTabsWithNoData: boolean;
    @Input() data: T[];

    @Input() exportTitle: string;

    @Input() showExport: boolean;
    @Input() showSearch: boolean;
    @Input() searchPredicate: FilterBarItemSearchPredicate<T>;

    @Input() showExportLabel: boolean;
    @Input() exportAsLandscape: boolean;

    @Input() reportLog: IReportHistory;

    @Input() tableColumns: IVTableColumn<any>[];
    @Input() tableRows: IVTableRow[];
    @Input() rowDataUniqueKey: string = 'Id';

    @Output() change = new EventEmitter<IAggregateFilterEvent<T>>();
    @Output() tableRowsChange = new EventEmitter<IVTableRow[]>();

    @ViewChild("remainingSpace") remainingSpace: ElementRef<HTMLElement>;

    @HostBinding("class.notabs") noTabs = false;

    // ---

    tableHeaders: IVTableHeader[];
    showAdditionalFilters = false;
    aggregateFilterEvent: IAggregateFilterEvent<T> = {} as any;

    moreFilterTypes: IVButtonDropDownItem<IFilterBarFilter>[] = [];
    filters: IFilterBarFilter[] = [];
    filterBarListFilters: IFilterBarListWithCount[] = [];

    tableRowsMap: Map<number, IVTableRow>;

    searchDebounce: ReturnType<typeof setTimeout>;

    updateCountsWorker: Worker;
    canEditColumns: boolean;

    // ---

    constructor(
        private readonly filterBarService: FilterBarService) {
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.showHideFilters();

        if (this.tabs && this.data) {
            this.updateTabCounts();
        }

        if (changes["tableRows"]) {
            this.tableRowsMap = new Map(this.tableRows?.map((row, index) => {
                const uniqueProperty = row.data[this.rowDataUniqueKey];
                if (uniqueProperty == undefined) {
                    console.warn(`${this.rowDataUniqueKey} key not found on row data. This will affect searching and filtering of table data.`);
                    return [index, row];
                }

                return [row.data[this.rowDataUniqueKey], row];
            }));

            this.setSearchPredicate(this.tableSearchPredicate);
        }

        if (changes["tableColumns"] && this.tableColumns?.length) {
            this.tableHeaders = this.tableColumns.map(column => column.header);
            this.canEditColumns = this.tableColumns.some(col => col.header.canEdit);
        }

        // Auto show search if we specify a predicate
        if (this.showSearch == undefined && this.searchPredicate) {
            this.showSearch = true;
        }

        if (changes["data"] || changes["tableRows"]) {
            // Trigger filtering and change event
            this.onFilterChange("sourcedata", this.data, { filterData: true });
        }

        this.noTabs = this.tabs == null || this.tabs.length == 0;
    }

    ngAfterViewInit(): void {

        //this.responsiveService.currentWindowWidth$.subscribe(() => {
        //    this.hideFiltersIfRequired();
        //});

        setTimeout(() => {
            if (this.remainingSpace.nativeElement.clientWidth < 200) {
                this.compact = true;
            }

            //this.hideFiltersIfRequired();

            this.showHideFilters();
        });
    }

    onSearch(text: string): void {
        clearTimeout(this.searchDebounce);

        this.searchDebounce = setTimeout(() => {
            // Small debounce to prevent potentially looping over thousands of rows on every keystroke
            let filterData = this.data && this.searchPredicate;
            this.onFilterChange("search", text?.toLocaleLowerCase(), { filterData: !!filterData });
        }, 150)
    }

    onTabSelected(tab: IFilterBarTab<T>): void {
        if (this.data) {
            this.setEventData("filter", tab.predicate);
            this.filterData();
        }

        this.onFilterChange("filter", tab.predicate);
    }

    onFiltersChange(filters: FilterChange[]): void {
        filters.forEach(f => {
            this.setEventData(f.type, f.event, f.options?.customName);
        });

        if (filters.some(f => f.options?.filterData)) {
            this.filterData();
            this.updateTabCounts();
        }

        this.tableRows && this.filterTableRows();

        this.change.emit(this.aggregateFilterEvent);

        // Ensures any focused buttons are blurred when clicked
        //if (this.aggregateFilterEvent.trigger != "search" && document.activeElement) {
        //    (document.activeElement as HTMLElement).blur();
        //}
    }

    onFilterChange(type: FilterTriggerType, event: any, options?: { customName?: string, filterData?: boolean }): void {

        this.setEventData(type, event, options?.customName);

        if (options?.filterData) {
            this.filterData();
            this.updateTabCounts();
        }

        this.tableRows && this.filterTableRows();

        this.change.emit(this.aggregateFilterEvent);

        // Ensures any focused buttons are blurred when clicked
        //if (this.aggregateFilterEvent.trigger != "search" && document.activeElement) {
        //    (document.activeElement as HTMLElement).blur();
        //}
    }

    setSearchPredicate(searchPredicate: FilterBarItemSearchPredicate<T>): void {
        this.searchPredicate = searchPredicate;
    }

    setEventData(type: FilterTriggerType, event: any, customName?: string): void {
        this.aggregateFilterEvent.trigger = type;

        if (type == "custom") {
            if (!customName) {
                console.error("Custom Name required to use custom FilterTriggerType");
                return;
            }

            if (!this.aggregateFilterEvent[type]) {
                this.aggregateFilterEvent[type] = [];
            }

            this.aggregateFilterEvent[type][customName.toString().toLocaleLowerCase()] = event;
        }
        else {
            this.aggregateFilterEvent[type] = event;
        }
    }

    childClosed(filter: IFilterBarFilter): void {

        if (!filter || filter.selected) {
            return;
        }

        filter.visible = false;
        this.updateFilterVisibility(filter);
    }

    registerFilter(filter: IFilterBarFilter): void {
        this.filters.push(filter);

        this.moreFilterTypes.push({
            label: filter.name,
            icon: filter.icon,
            data: filter
        });

        this.showAdditionalFilters = this.filters.length != 0;
        //filter.visible = true;
        this.updateFilterVisibility(filter);

        // This is a beta piece of code designed to show all filters if the space is available, currently coded out to alleviate test burden
        // Will be added back in later
        //this.hideFiltersIfRequired();
    }

    onTableColumnEditClicked(): void {
        this.filterBarService.toggleTableColumnEditor(true, this.tableColumns);
    }

    private hideFiltersIfRequired(): void {
        var width = this.remainingSpace?.nativeElement.getBoundingClientRect().width;

        var itemsToShow = Math.floor(width / 200) + this.filters.filter(f => f.visible).length;
        if (this.showAdditionalFilters && width < 250 || width < 200) {
            itemsToShow--;
        }

        // Reset
        this.filters.forEach((f, i) => {
            if (i < itemsToShow) {
                f.visible = true;
            }
            else {
                f.visible = false;
            }

            //console.log(f.name, i, itemsToShow, f.visible);
            this.updateFilterVisibility(f);
        });

        this.showAdditionalFilters = itemsToShow < this.filters.length;
    }

    registerFilterBarList(list: IFilterBarListWithCount): void {
        this.filterBarListFilters.push(list);

        // If we already have table rows, we need to initiate our list creation
        if (this.tableRows?.length) {
            list.updateList(this.data);
            this.updateFilterBarCounts(list);
        }
    }

    onMoreFilterClick(dropdownItem: IVButtonDropDownItem<IFilterBarFilter>): void {
        dropdownItem.data.visible = !dropdownItem.data.visible;
        this.updateFilterVisibility(dropdownItem.data);

        // Auto click it, so it feels nice
        if (dropdownItem.data.visible) {
            dropdownItem.data.elementRef.nativeElement.querySelector<HTMLElement>("button").click();
        }

        if (!dropdownItem.data.visible) {
            dropdownItem.data.selected = null;

            this.onFilterChange(
                dropdownItem.data['type'], 
                dropdownItem.data.selected, 
                { customName: dropdownItem.data['defaultLabel'], filterData: true }
            );
        }
    }

    filterChildData<T>(filterBarFilter: IFilterBarFilter<T>, filterData: T[]): T[] {
        if (!this.data?.length) {

            console.warn("FilterBar: No data provided");
            return [];
        }

        // O(n*m) - needs refactoring
        return filterData.filter(filterData => this.data.some(d => filterBarFilter.filterPredicate(d, filterData)));
    }

    updateFilterVisibility(filter: IFilterBarFilter) {
        filter.elementRef.nativeElement.style.display = this.compact && !filter.visible ? 'none' : null;
    }

    // ---

    private showHideFilters(): void {
        this.filters.forEach(f => {
            this.updateFilterVisibility(f);
        });
    }

    updateTabCounts(): void {
        if (!this.tabs) {
            return;
        }

        // Reset count
        this.tabs.forEach(t => t.count = 0);

        // Add up tab count
        if (!this.data) {
            return;
        }

        // We use global data for this
        this.data.forEach(d => {
            if (!this.filterDataPredicate(d, true)) {
                return;
            }

            this.tabs.forEach(tab => {
                if (tab.predicate(d)) {
                    tab.count++;
                }
            });
        });
    }

    private updateFilterListCounts(rebuildList = false): void {

        this.filterBarListFilters.forEach(l => {
            l.maxListItemCount = 0;

            if (rebuildList) {
                l.updateList(this.data);
            }
            else {
                l.items.forEach(item => {
                    item.count = 0;
                    item.countAsPercentageOfMax = 0;
                });
            }
        });

        // New logic, ALL filters that arent ourselves must pass
        this.updateAllFilterBarCounts();

        if (rebuildList) {
            // Now we sort
            this.filterBarListFilters.forEach(list => {
                list.items.sort((a, b) => {
                    return b.count - a.count;
                });
            });
        }
    }

    private updateAllFilterBarCounts(): void {
        this.filterBarListFilters.forEach(list => {

            this.updateFilterBarCounts(list);
        });
    }

    private updateFilterBarCounts(list: IFilterBarListWithCount): void {
        const otherFilterListsToinclude = this.filterBarListFilters.filter(f => f != list && f.selectedItems.length > 0);
        const itemLength = list.items.length;
        list.maxListItemCount = 0;

        for (var i = 0; i < itemLength; i++) {
            const item = list.items[i];

            this.updateItemCount(item, list, otherFilterListsToinclude);
            list.maxListItemCount = Math.max(list.maxListItemCount, item.count);
        };

        list.items.forEach(item => {
            item.countAsPercentageOfMax = list.maxListItemCount ? item.count * 100.0 / list.maxListItemCount : 0;
        });
    }

    private updateItemCount(item: IVerticalFilterListItem, itemList: IFilterBarListWithCount, otherFilterListsToinclude: IFilterBarListWithCount[]): void {
        if (otherFilterListsToinclude) {
            otherFilterListsToinclude = this.filterBarListFilters.filter(f => f != itemList && f.selectedItems.length > 0);
        }

        item.count = this.aggregateFilterEvent.filteredData.filter(d => {
            const itemIncluded = item.predicate(d);

            if (!itemIncluded) {
                return false;
            }

            if (otherFilterListsToinclude.length == 0) {
                return true;
            }

            const filterConditionsPassStatuses = otherFilterListsToinclude.map(filterList => {
                const selectedItems = filterList.items.filter(f => f.selected);

                if (selectedItems.length === 0) {
                    return true;
                }

                const result = filterList.multiSelectMode === 'every-filter'
                    ? selectedItems.every(f => f.predicate(d))
                    : selectedItems.some(f => f.predicate(d));

                return result;
            });

            return filterConditionsPassStatuses.every(c => c);
        }).length;
    }

    private filterData(): void {
        if (!this.data) {
            return;
        }

        // Filter Pass 1, tabs, searches, filters
        if (this.hasFilterApplied()) {
            this.aggregateFilterEvent.filteredData = this.data.filter(d => this.filterDataPredicate(d));

            // Update filter list counts
            this.updateFilterListCounts(this.aggregateFilterEvent.trigger == "sourcedata");

            // Filter Pass 2, vertical lists (in-page filtering)
            this.aggregateFilterEvent.filteredData = this.filterDataByListFilters(this.aggregateFilterEvent.filteredData);
        }
        else {
            this.aggregateFilterEvent.filteredData = this.data;

            // Update filter list counts
            this.updateFilterListCounts(this.aggregateFilterEvent.trigger == "sourcedata");
        }

    }

    private hasFilterApplied(): boolean {
        return this.aggregateFilterEvent.filter != undefined || this.aggregateFilterEvent.search != undefined || this.aggregateFilterEvent.custom != undefined;
    }

    private filterDataPredicate(data: T, ignoreTabFilter = false): boolean {
        // Filter
        if (!ignoreTabFilter && this.aggregateFilterEvent.filter !== undefined && !this.aggregateFilterEvent.filter(data)) {
            return false;
        }

        // Text search
        if (this.searchPredicate && this.aggregateFilterEvent.search && !this.searchPredicate(data, this.aggregateFilterEvent.search)) {
            return false;
        }

        // Child filters
        for (var f of this.filters) {

            if (f.selected && f.filterPredicate && !f.filterPredicate(data, f.selected)) {
                return false;
            }
        }

        return true;
    }

    private filterDataByListFilters(data: T[]): T[] {
        const filterLists = this.filterBarListFilters
            .map(filterList => new FilterBarListIterable(filterList.multiSelectMode, filterList.selectedItems))
            .filter(filterList => filterList.selectedItems.length > 0);

        const result = data.filter(d => {
            return filterLists.every(fl => {
                const result = fl.mode === 'every-filter'
                    ? fl.selectedItems.every(f => { return f.predicate(d) })
                    : fl.selectedItems.some(f => { return f.predicate(d) });

                return result;
            })
        });

        return result;
    }

    private tableSearchPredicate(d, searchString): boolean {
        if (!this.tableColumns) {
            return;
        }

        const tableRow = this.tableRowsMap.get(d[this.rowDataUniqueKey]);

        if (!tableRow) {
            return false;
        }

        if (tableRow.searchString.includes(searchString.toLowerCase())) {
            return true;
        }
    }

    private filterTableRows(): void {
        if (!this.tableRows) {
            return;
        }

        this.tableRows.forEach(row => row.hide = true);

        if (!this.aggregateFilterEvent?.filteredData?.length) {
            this.filterBarService.dataChanged.next();
            return;
        }

        this.aggregateFilterEvent.filteredData.forEach(data => {
            const row = this.tableRowsMap.get(data[this.rowDataUniqueKey]);

            if (row) {
                row.hide = false;
            }
        });

        this.filterBarService.dataChanged.next();
    }
}
